import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatMoney } from "../lib/api";
import { useApp } from "../context/AppContext";
import { CheckCircle2, QrCode, Building2, CreditCard, Smartphone, Truck, PartyPopper } from "lucide-react";

const OrderSuccess = () => {
  const { id } = useParams();
  const { t, lang, currency, settings } = useApp();
  const [order, setOrder] = useState(null);
  const [paying, setPaying] = useState(false);

  const load = () => api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => {});
  useEffect(() => { load(); }, [id]);

  if (!order) return <div className="max-w-3xl mx-auto px-6 py-20 text-center">Loading…</div>;

  const ins = order.payment_instructions || {};
  const isPaid = order.payment_status === "paid";

  const simulate = async () => {
    setPaying(true);
    try {
      await api.post(`/payment/${order.id}/confirm`);
      await load();
    } finally { setPaying(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12">
      <div className="bg-white rounded-2xl border border-border/60 p-8 text-center">
        {isPaid ? (
          <PartyPopper className="w-14 h-14 text-holy-green mx-auto mb-3" />
        ) : (
          <CheckCircle2 className="w-14 h-14 text-holy-red mx-auto mb-3" />
        )}
        <h1 className="font-serif text-3xl text-holy-red mb-1">{t("order_success")}</h1>
        <p className="text-muted-foreground text-sm mb-4">{t("order_number")}: <span className="font-mono font-semibold text-foreground" data-testid="order-number">{order.order_number}</span></p>
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cream text-sm">
          {isPaid ? <span className="text-holy-green font-semibold">● {t("paid")}</span> : <span className="text-holy-gold-dark font-semibold">● {t("pending")}</span>}
        </div>
      </div>

      {/* Payment Instructions */}
      {!isPaid && (
        <div className="bg-white rounded-2xl border border-border/60 p-6 mt-6">
          <h3 className="font-serif text-xl mb-4">{t("pay_now")}</h3>
          {ins.method === "qris" && (
            <div className="text-center">
              <div className="inline-block bg-cream p-6 rounded-2xl border border-border">
                <QrCode className="w-44 h-44 text-foreground mx-auto" />
                <div className="text-xs mt-3 font-mono break-all max-w-xs">{ins.qr_string?.slice(0, 60)}…</div>
              </div>
              <p className="text-sm text-muted-foreground mt-4">{ins.display}</p>
            </div>
          )}
          {ins.method === "bank_transfer" && (
            <div className="bg-cream rounded-xl p-5 text-center">
              <Building2 className="w-10 h-10 text-holy-red mx-auto mb-2" />
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Virtual Account {ins.bank}</div>
              <div className="font-mono text-2xl font-semibold my-2 text-holy-red tracking-wider" data-testid="va-number">{ins.va_number}</div>
              <p className="text-sm text-muted-foreground">{ins.display}</p>
            </div>
          )}
          {ins.method === "credit_card" && (
            <div className="text-center">
              <CreditCard className="w-10 h-10 text-holy-red mx-auto mb-3" />
              <p className="text-sm text-muted-foreground mb-4">{ins.display}</p>
              <a href={ins.redirect_url} target="_blank" rel="noreferrer" className="inline-flex bg-holy-red text-ivory rounded-full px-6 py-2.5 text-sm">
                {lang === "id" ? "Buka Halaman Pembayaran" : "Open Payment Page"}
              </a>
            </div>
          )}
          {["gopay", "ovo", "dana", "shopeepay"].includes(ins.method) && (
            <div className="text-center">
              <Smartphone className="w-10 h-10 text-holy-red mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">{ins.display}</p>
            </div>
          )}

          <div className="border-t border-border/60 mt-6 pt-4 text-center">
            <p className="text-xs text-muted-foreground mb-3">⚡ {lang === "id" ? "Mode demo — klik untuk simulasikan pembayaran sukses" : "Demo mode — click to simulate successful payment"}</p>
            <button
              data-testid="simulate-payment-btn"
              onClick={simulate}
              disabled={paying}
              className="bg-holy-green hover:bg-holy-green-dark text-ivory rounded-full px-6 py-2.5 text-sm disabled:opacity-50"
            >
              {paying ? "..." : t("simulate_payment")}
            </button>
          </div>
        </div>
      )}

      {/* Order Details */}
      <div className="bg-white rounded-2xl border border-border/60 p-6 mt-6">
        <h3 className="font-serif text-xl mb-4">{lang === "id" ? "Detail Pesanan" : "Order Details"}</h3>
        <div className="space-y-2 mb-4">
          {order.items.map((it, i) => (
            <div key={i} className="flex justify-between text-sm">
              <span>{it.name} × {it.quantity}</span>
              <span>{formatMoney(it.price_idr * it.quantity, it.price_usd * it.quantity, currency)}</span>
            </div>
          ))}
        </div>
        <hr className="border-border my-3" />
        <div className="text-sm space-y-1">
          <div className="flex justify-between"><span>{t("subtotal")}</span><span>{formatMoney(order.subtotal_idr, order.subtotal_usd, currency)}</span></div>
          <div className="flex justify-between"><span><Truck className="inline w-4 h-4 mr-1" />{order.shipping_courier} · {order.shipping_service}</span><span>{formatMoney(order.shipping_cost_idr, order.shipping_cost_usd, currency)}</span></div>
          <div className="flex justify-between font-semibold text-lg text-holy-red pt-2"><span>{t("total")}</span><span>{formatMoney(order.total_idr, order.total_usd, currency)}</span></div>
        </div>
        <div className="border-t border-border/60 mt-5 pt-4 text-sm text-muted-foreground">
          <div className="font-semibold text-foreground mb-1">{lang === "id" ? "Dikirim ke" : "Ship to"}</div>
          <div>{order.customer_name} · {order.customer_phone}</div>
          <div>{order.address}, {order.city}, {order.province} {order.postal_code}, {order.country}</div>
        </div>
      </div>

      <div className="text-center mt-8 flex flex-col sm:flex-row gap-3 justify-center">
        <Link to={`/track?order=${order.order_number}`} className="inline-flex items-center justify-center gap-2 bg-holy-green text-ivory rounded-full px-6 py-2.5 text-sm hover:-translate-y-0.5 transition-all" data-testid="track-order-link">
          📦 {lang === "id" ? "Lacak Pesanan Ini" : "Track This Order"}
        </Link>
        <a
          href={(() => {
            const wa = (settings?.whatsapp || "+6281286899958").replace(/[^\d]/g, "");
            const text = lang === "id"
              ? `Halo Holy Flowy! Saya baru saja melakukan pesanan dengan nomor ${order.order_number}. Mohon dikonfirmasi ya. Terima kasih! 🎄`
              : `Hi Holy Flowy! I've just placed an order ${order.order_number}. Please confirm. Thanks! 🎄`;
            return `https://wa.me/${wa}?text=${encodeURIComponent(text)}`;
          })()}
          target="_blank" rel="noreferrer"
          data-testid="wa-confirm-order"
          className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white rounded-full px-6 py-2.5 text-sm hover:-translate-y-0.5 transition-all"
        >
          💬 {lang === "id" ? "Konfirmasi via WhatsApp" : "Confirm via WhatsApp"}
        </a>
        <Link to="/shop" className="text-holy-red font-medium hover:underline self-center">← {t("continue_shopping")}</Link>
      </div>
    </div>
  );
};

export default OrderSuccess;
